import { useState } from "react";
import { Banknote, Receipt } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { fmtDate, uuid } from "@/lib/format";
import { billEncounter, patientBalance, patientPayments, patientSales } from "@/lib/clinic";
import { BigButton, Chip, Field, inputCls } from "@/components/ui-kit";

export default function PatientBillingTab({ patient, history }) {
  const { t, view, dispatch, can, money, lang } = useApp();
  const balance = patientBalance(view, patient);
  const sales = patientSales(view, patient.id);
  const payments = patientPayments(view, patient.id);
  const unbilled = history.encounters.filter((e) => !e.billed_sale_id && e.total > 0);
  const [amount, setAmount] = useState("");
  const [pay, setPay] = useState("cash");
  const collect = () => {
    const a = Number(amount);
    if (!a || a <= 0) return toast.error(t("invalid_amount"));
    if (dispatch("payment.create", { id: uuid(), customer_id: patient.customer_id || patient.id, customer_name: patient.name, amount: a, method: "cash", note: t("patient") })) { toast.success(`${t("payment_received")} · ${money(a)}`); setAmount(""); }
  };
  const billNow = (e) => {
    const sale = billEncounter({ dispatch, view, t, patient, encounter: e, appointment: view.appointments?.[e.appointment_id], pay });
    if (sale) toast.success(`${t("billed")} · ${money(sale.total)}`);
  };
  return (
    <div className="space-y-3" data-testid="patient-billing-tab">
      <div className={`rounded border p-3 flex items-center justify-between ${balance > 0 ? "border-[#C7353B] bg-[#FDE8E8]" : "border-[#CCCCCC] bg-white"}`}>
        <span className="font-bold">{t("patient_balance")}</span><span dir="ltr" className={`text-xl font-black ${balance > 0 ? "text-[#C7353B]" : "text-[#1F9D55]"}`} data-testid="patient-balance">{money(balance)}</span>
      </div>
      {unbilled.length > 0 && (
        <div className="rounded border border-[#E67E22] bg-white p-3 space-y-2" data-testid="unbilled-list">
          <div className="font-bold text-sm text-[#E67E22]">{t("pending_bills")} ({unbilled.length})</div>
          <div className="flex gap-2">{["cash", "credit"].map((m) => <Chip key={m} active={pay === m} onClick={() => setPay(m)} data-testid={`billing-pay-${m}`}>{t(`pay_${m}`)}</Chip>)}</div>
          {unbilled.map((e) => (
            <div key={e.id} className="flex items-center gap-2 text-sm border-t border-[#EEE] pt-2" data-testid="unbilled-row">
              <span dir="ltr" className="text-[#666]">{fmtDate(e.date, lang, false)}</span><span className="flex-1 truncate">{e.doctor_name} · {e.services.map((s) => s.name).join(", ")}</span><span dir="ltr" className="font-bold">{money(e.total)}</span>
              {can("sell") && <button type="button" onClick={() => billNow(e)} className="h-9 px-3 rounded bg-[#1F9D55] text-white text-xs font-bold flex items-center gap-1" data-testid="unbilled-bill-btn"><Receipt className="w-4 h-4" />{t("bill_consultation")}</button>}
            </div>
          ))}
        </div>
      )}
      {can("sell") && balance > 0 && (
        <div className="rounded border border-[#CCCCCC] bg-white p-3 grid grid-cols-[1fr_auto_auto] gap-2 items-end" data-testid="collect-form">
          <Field label={t("amount")}><input dir="ltr" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} data-testid="collect-amount-input" /></Field>
          <BigButton variant="soft" onClick={() => setAmount(String(balance))} className="!h-12" data-testid="collect-full-btn">{t("pay_all")}</BigButton>
          <BigButton variant="green" onClick={collect} className="!h-12" data-testid="collect-btn"><Banknote className="w-5 h-5" />{t("collect")}</BigButton>
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-3">
        <div>
          <h4 className="font-bold text-sm mb-1">{t("invoices")} ({sales.length})</h4>
          <div className="space-y-1" data-testid="patient-sales">
            {sales.map((s) => <div key={s.id} className="rounded border border-[#E5E5E5] bg-white px-3 py-2 text-sm flex gap-2" data-testid="patient-sale-row"><span dir="ltr" className="text-[#666]">{fmtDate(s.created_at, lang, false)}</span><span className="flex-1 truncate">{s.doctor_name || s.items.map((i) => i.name).join(", ")}</span><span className={`px-1.5 rounded text-xs font-bold text-white ${s.payment === "cash" ? "bg-[#1F9D55]" : "bg-[#C7353B]"}`}>{t(`pay_${s.payment}`)}</span><span dir="ltr" className="font-bold">{money(s.total)}</span></div>)}
            {!sales.length && <p className="text-sm text-muted-foreground">—</p>}
          </div>
        </div>
        <div>
          <h4 className="font-bold text-sm mb-1">{t("payments")} ({payments.length})</h4>
          <div className="space-y-1" data-testid="patient-payments">
            {payments.map((p) => <div key={p.id} className="rounded border border-[#E5E5E5] bg-white px-3 py-2 text-sm flex gap-2" data-testid="patient-payment-row"><span dir="ltr" className="text-[#666]">{fmtDate(p.created_at, lang)}</span><span className="flex-1">{p.staff_name}</span><span dir="ltr" className="font-bold text-[#1F9D55]">{money(p.amount)}</span></div>)}
            {!payments.length && <p className="text-sm text-muted-foreground">—</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
